import { AxiosError, CancelToken } from 'axios';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../Common/api/ABHub/ABHubGlobalConfig';
import { agpAxiosClient } from '../../Common/api/ABHub/ABHubTokenConfig';
import { RequestResult } from '../../AGCP/models/AirGapMessage';

/**
 * Whether a given AAD application principal has access to the SovereignReleaseStatus database.
 * Mirrors the SRF `KustoAccessStatus` model.
 */
export type KustoAccessStatus = {
    applicationId?: string;
    tenantId?: string;
    hasAccess: boolean;
    role?: string;
    displayName?: string;
};

export type KustoTenant = {
    tenantId: string;
    displayName: string;
};

/**
 * Payload for requesting viewer access for an AAD application.
 */
export type GrantKustoAccessRequest = {
    applicationId: string;
    tenantId: string;
    displayName: string;
    justification?: string;
};

/**
 * Payload for revoking an AAD application's access.
 */
export type RevokeKustoAccessRequest = {
    applicationId: string;
    tenantId: string;
};

/**
 * A principal currently assigned a role on the database. Mirrors the SRF `KustoPrincipal` model.
 */
export type KustoPrincipal = {
    role?: string;
    principalType?: string;
    displayName?: string;
    applicationId?: string;
    tenantId?: string;
    objectId?: string;
    fqn?: string;
};

/**
 * A Kusto access audit record. Mirrors the SRF `KustoAuditRecord` model.
 */
export type KustoAuditRecord = {
    timestampUtc: string;
    action?: string;
    applicationId?: string;
    tenantId?: string;
    displayName?: string;
    justification?: string;
    reviewNotes?: string;
    requestedBy?: string;
    succeeded: boolean;
    message?: string;
};

const getSrfUrl = (): string => {
    const srfUrl = getConfig(GlobalConfigProperties.SrfUrl);
    if (!srfUrl) {
        throw new Error(
            'SRF (ABSM) URL is not configured. Add an "SRF": { "Url": "..." } ' +
                'section to the AB Hub appsettings for this environment.'
        );
    }
    return srfUrl;
};

/**
 * Pulls a human-readable message out of a failed SRF call. SRF returns a
 * `RequestResult` envelope in the error body with a `message` field.
 */
export const extractKustoError = (error: unknown): string => {
    const axiosError = error as AxiosError<RequestResult<KustoAccessStatus>>;
    return (
        axiosError?.response?.data?.message ??
        (error as Error)?.message ??
        'An unexpected error occurred.'
    );
};

export const listKustoTenants = async (
    cancelToken?: CancelToken
): Promise<RequestResult<KustoTenant[]>> => {
    const response = await agpAxiosClient.get<RequestResult<KustoTenant[]>>(
        `${getSrfUrl()}/api/kusto/requests/tenants`,
        { cancelToken }
    );
    return response.data;
};

/**
 * Checks whether the given AAD application (client) ID already has access to the
 * SovereignReleaseStatus Kusto database.
 */
export const getKustoAppAccess = async (
    applicationId: string,
    tenantId: string,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAccessStatus>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<RequestResult<KustoAccessStatus>>(
        `${srfUrl}/api/kusto/access`,
        {
            params: { applicationId, tenantId },
            cancelToken,
        }
    );

    return response.data;
};

/**
 * Grants the given AAD application viewer access to the SovereignReleaseStatus Kusto database.
 * Requires the caller's SRM role to permit provisioning (DRI / admin).
 */
export const grantKustoAppAccess = async (
    request: GrantKustoAccessRequest,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAccessStatus>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.post<
        RequestResult<KustoAccessStatus>
    >(`${srfUrl}/api/kusto/access`, request, { cancelToken });

    return response.data;
};

/**
 * Revokes the given AAD application's viewer access. (Admin action.)
 */
export const revokeKustoAppAccess = async (
    request: RevokeKustoAccessRequest,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAccessStatus>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.post<
        RequestResult<KustoAccessStatus>
    >(`${srfUrl}/api/kusto/access/revoke`, request, { cancelToken });

    return response.data;
};

/**
 * Lists all principals currently assigned a role on the database. (Admin view.)
 */
export const listKustoPrincipals = async (
    cancelToken?: CancelToken
): Promise<RequestResult<KustoPrincipal[]>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<RequestResult<KustoPrincipal[]>>(
        `${srfUrl}/api/kusto/principals`,
        { cancelToken }
    );

    return response.data;
};

/**
 * Returns the most recent Kusto access audit records. (Admin view.)
 */
export const getKustoAuditTrail = async (
    maxRecords: number = 100,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAuditRecord[]>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<
        RequestResult<KustoAuditRecord[]>
    >(`${srfUrl}/api/kusto/audit`, { params: { maxRecords }, cancelToken });

    return response.data;
};

/** Review states a request moves through. */
export const KustoRequestStatus = {
    pending: 'Pending',
    approved: 'Approved',
    denied: 'Denied',
    cancelled: 'Cancelled',
} as const;

/** Actions a request can ask for. */
export const KustoRequestAction = {
    grant: 'Grant',
    revoke: 'Revoke',
} as const;

/**
 * A request as the requester sees it. Deliberately carries no reviewer identity or notes —
 * the SRF endpoint does not return them.
 */
export type MyKustoAccessRequest = {
    id?: string;
    action?: string;
    status?: string;
    failureMessage?: string | null;
    applicationId?: string;
    tenantId?: string;
    displayName?: string;
    justification?: string;
    requestedOn: string;
    reviewedOn?: string;
    relatedRequestId?: string;
};

/** A request as an SRM admin sees it, including review detail. */
export type KustoAccessRequestRecord = MyKustoAccessRequest & {
    requestedBy?: string;
    reviewedBy?: string;
    reviewNotes?: string;
};

/** Payload for submitting a new access request. */
export type SubmitKustoAccessRequest = {
    applicationId: string;
    tenantId: string;
    displayName: string;
    justification: string;
};

/**
 * Queues a request for an AAD application to be granted access. A request submitted by an
 * SRM admin is applied immediately.
 */
export const submitKustoAccessRequest = async (
    request: SubmitKustoAccessRequest,
    cancelToken?: CancelToken
): Promise<RequestResult<MyKustoAccessRequest>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.post<
        RequestResult<MyKustoAccessRequest>
    >(`${srfUrl}/api/kusto/requests`, request, { cancelToken });

    return response.data;
};

/** Returns the caller's own requests and their current status. */
export const listMyKustoAccessRequests = async (
    cancelToken?: CancelToken
): Promise<RequestResult<MyKustoAccessRequest[]>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<
        RequestResult<MyKustoAccessRequest[]>
    >(`${srfUrl}/api/kusto/requests/mine`, { cancelToken });

    return response.data;
};

/** Withdraws one of the caller's own pending requests. */
export const cancelMyKustoAccessRequest = async (
    requestId: string,
    cancelToken?: CancelToken
): Promise<RequestResult<MyKustoAccessRequest>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.post<
        RequestResult<MyKustoAccessRequest>
    >(`${srfUrl}/api/kusto/requests/${requestId}/cancel`, null, {
        cancelToken,
    });

    return response.data;
};

/** Requests removal of access the caller previously requested. */
export const requestKustoAccessRemoval = async (
    requestId: string,
    justification: string,
    cancelToken?: CancelToken
): Promise<RequestResult<MyKustoAccessRequest>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.post<
        RequestResult<MyKustoAccessRequest>
    >(
        `${srfUrl}/api/kusto/requests/${requestId}/revoke`,
        { justification },
        { cancelToken }
    );

    return response.data;
};

/**
 * Read-only check of whether an application already has access, for the self-service page.
 * Unlike `getKustoAppAccess` this does not require the admin role.
 */
export const lookupKustoAccessForRequest = async (
    applicationId: string,
    tenantId: string,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAccessStatus>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<RequestResult<KustoAccessStatus>>(
        `${srfUrl}/api/kusto/requests/lookup`,
        { params: { applicationId, tenantId }, cancelToken }
    );

    return response.data;
};

/** Lists queued access requests. (Admin view.) */
export const listKustoAccessRequests = async (
    status?: string,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAccessRequestRecord[]>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<
        RequestResult<KustoAccessRequestRecord[]>
    >(`${srfUrl}/api/kusto/requests`, {
        params: status ? { status } : undefined,
        cancelToken,
    });

    return response.data;
};

/** Returns the number of requests awaiting review. (Admin view.) */
export const countPendingKustoAccessRequests = async (
    cancelToken?: CancelToken
): Promise<RequestResult<{ pending: number }>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<
        RequestResult<{ pending: number }>
    >(`${srfUrl}/api/kusto/requests/count`, { cancelToken });

    return response.data;
};

/** Returns every request targeting an application, for reviewer context. (Admin view.) */
export const listKustoRequestsForApplication = async (
    applicationId: string,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAccessRequestRecord[]>> => {
    const srfUrl = getSrfUrl();
    const response = await agpAxiosClient.get<
        RequestResult<KustoAccessRequestRecord[]>
    >(`${srfUrl}/api/kusto/requests/application/${applicationId}`, {
        cancelToken,
    });

    return response.data;
};

/** Approves or denies a queued request. (Admin action.) */
export const reviewKustoAccessRequest = async (
    requestId: string,
    approve: boolean,
    reviewNotes?: string,
    cancelToken?: CancelToken
): Promise<RequestResult<KustoAccessRequestRecord>> => {
    const srfUrl = getSrfUrl();
    const action = approve ? 'approve' : 'deny';
    const response = await agpAxiosClient.post<
        RequestResult<KustoAccessRequestRecord>
    >(
        `${srfUrl}/api/kusto/requests/${requestId}/${action}`,
        { reviewNotes },
        { cancelToken }
    );

    return response.data;
};
